import { PageSkeleton } from '@/components/layout/page-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import {
	CHROME_SURFACE,
	RAIL_SURFACE,
	SHELL_CONTENT_FRAME,
	SHELL_HEADER_HEIGHT,
	SHELL_RAIL_FRAME,
	SHELL_TOPBAR_FRAME,
} from '@/components/ui/shell/rail-shell';
import { platformBrandTheme } from '@/lib/brand/resolve-brand-theme';
import { cn } from '@/lib/utils';
import type { CSSProperties } from 'react';

/** Enough rail items to fill the visible column; the exact count never shifts the content area. */
const RAIL_ITEM_COUNT = 6;

/**
 * A custom (whitelabel) domain exists to hide the platform brand, and its own brand has not
 * resolved yet while this skeleton shows - so its chrome paints a neutral grey instead of the
 * platform colour. The app domain (and the e2e host) keeps the platform chrome.
 */
const isPlatformHost = (): boolean =>
	import.meta.env.VITE_APP_ENV === 'e2e' ||
	(typeof window !== 'undefined' && window.location.hostname.includes(import.meta.env.VITE_APP_DOMAIN));

const NEUTRAL_CHROME_THEME: CSSProperties = { '--primary': 'oklch(0.72 0 0)' } as CSSProperties;

/**
 * The app shell before it can be rendered for real: rail strip, topbar strip, and the content card,
 * at the dimensions `AppShell` gives them (shared frame constants, header-height variable, and
 * surfaces), so the swap to the real shell repaints in place instead of moving anything.
 *
 * This is the whole point of the boot sequence being shell → content: every gate on the way in
 * (hydrate resolving, the workspace not selected yet, the route chunk still loading) renders this
 * rather than a fullscreen spinner over a blank page, so the user sees one stable frame.
 *
 * It renders above the global store - no store, no i18n, no permissions - so it can only be built
 * from primitives, not from `AppShell`'s own parts.
 */
export function AppShellSkeleton() {
	const platformHost = isPlatformHost();

	return (
		<div
			data-testid="app-shell-skeleton"
			data-brand-theme={platformHost ? '' : undefined}
			style={platformHost ? platformBrandTheme() : NEUTRAL_CHROME_THEME}
			className={cn('flex h-dvh', SHELL_HEADER_HEIGHT)}
		>
			<nav aria-hidden className={cn('hidden md:flex', SHELL_RAIL_FRAME, RAIL_SURFACE)}>
				{Array.from({ length: RAIL_ITEM_COUNT }).map((_, index) => (
					<div key={index} className="flex flex-col items-center gap-1">
						<Skeleton className="size-10 rounded-full bg-white/10" />
						<Skeleton className="h-4 w-12 bg-white/10" />
					</div>
				))}
			</nav>
			<div className={cn('flex min-w-0 flex-1 flex-col', CHROME_SURFACE)}>
				<div className={cn(SHELL_TOPBAR_FRAME, 'shrink-0 gap-2')}>
					<Skeleton className="h-7 w-40 rounded-md" />
					<Skeleton className="ml-auto size-7 rounded-md" />
					<Skeleton className="size-7 rounded-full" />
				</div>
				<div className="flex min-h-0 flex-1">
					<main className={SHELL_CONTENT_FRAME}>
						<PageSkeleton />
					</main>
				</div>
			</div>
		</div>
	);
}
